import { captureException } from '@sentry/react'
import { Tile, FeedRef, TileSnapshot, getOwnerKey } from '../types/feedRef'
import {
  MULTIVIEW_LIMITS,
  SESSION_COLORS,
  PERSISTENCE_VERSION,
} from '../config'

const ENTRY_SEP = ','
const FIELD_SEP = '|'
const REF_SEP = ':'
const SNAPSHOT_STORAGE_PREFIX = 'multiview-snapshots:'

export interface DecodedState {
  tiles: Tile[]
  colors: Record<string, number>
}

const enc = encodeURIComponent
const dec = decodeURIComponent

const safeDecode = (value: string): string | null => {
  try {
    return dec(value)
  } catch {
    return null
  }
}

const encodeRef = (ref: FeedRef): string => {
  if (ref.scope === 'project') {
    return [
      'p',
      enc(ref.projectId),
      enc(ref.componentId),
      enc(ref.padName),
    ].join(REF_SEP)
  }
  // Session refs reserve a projectId slot (empty for runtime-only feeds) so the
  // optional project parent survives a round-trip.
  return [
    's',
    enc(ref.sessionId),
    ref.projectId ? enc(ref.projectId) : '',
    enc(ref.componentId),
    enc(ref.padName),
  ].join(REF_SEP)
}

const decodeRef = (raw: string): FeedRef | null => {
  const parts = raw.split(REF_SEP)
  const scope = parts[0]
  try {
    if (scope === 'p') {
      if (parts.length !== 4) return null
      const projectId = dec(parts[1])
      const componentId = dec(parts[2])
      const padName = dec(parts[3])
      if (!projectId || !componentId || !padName) return null
      return { scope: 'project', projectId, componentId, padName }
    }
    if (scope === 's') {
      // 5 fields: sessionId, projectId slot, componentId, padName.
      if (parts.length === 5) {
        const sessionId = dec(parts[1])
        const componentId = dec(parts[3])
        const padName = dec(parts[4])
        if (!sessionId || !componentId || !padName) return null
        if (parts[2]) {
          return {
            scope: 'session',
            sessionId,
            projectId: dec(parts[2]),
            componentId,
            padName,
          }
        }
        return { scope: 'session', sessionId, componentId, padName }
      }
      // 4 fields: legacy V2 session ref without a projectId slot.
      if (parts.length === 4) {
        const sessionId = dec(parts[1])
        const componentId = dec(parts[2])
        const padName = dec(parts[3])
        if (!sessionId || !componentId || !padName) return null
        return { scope: 'session', sessionId, componentId, padName }
      }
    }
  } catch {
    // decodeURIComponent throws on malformed input; treat as unparseable.
    return null
  }
  return null
}

export const encodeTiles = (
  tiles: Tile[],
  colors: Record<string, number>
): string => {
  if (!tiles || tiles.length === 0) return ''
  return tiles
    .map((tile) => {
      const ref = encodeRef(tile.ref)
      const ownerKey = getOwnerKey(tile.ref)
      const colorIdx = colors[ownerKey] ?? ''
      return [PERSISTENCE_VERSION, enc(tile.tileId), ref, colorIdx].join(
        FIELD_SEP
      )
    })
    .join(ENTRY_SEP)
}

export const decodeTiles = (raw: string): DecodedState => {
  if (!raw || raw.trim() === '') return { tiles: [], colors: {} }
  try {
    const tiles: Tile[] = []
    const colors: Record<string, number> = {}
    const entries = raw.split(ENTRY_SEP).filter(Boolean)
    for (const entry of entries) {
      const fields = entry.split(FIELD_SEP)
      if (fields.length < 3 || fields[0] !== PERSISTENCE_VERSION) continue
      const tileId = fields[1] ? safeDecode(fields[1]) : null
      const refStr = fields[2]
      const colorIdxStr = fields[3]
      if (!tileId || !refStr) continue
      const ref = decodeRef(refStr)
      if (!ref) continue
      const snapshot: TileSnapshot = { displayName: '', componentType: null }
      tiles.push({ tileId, ref, snapshot })
      if (colorIdxStr !== undefined && colorIdxStr !== '') {
        const colorIdx = parseInt(colorIdxStr, 10)
        if (
          !isNaN(colorIdx) &&
          colorIdx >= 0 &&
          colorIdx < SESSION_COLORS.length
        ) {
          colors[getOwnerKey(ref)] = colorIdx
        }
      }
      if (tiles.length >= MULTIVIEW_LIMITS.maxFeeds) break
    }
    return { tiles, colors }
  } catch (error) {
    captureException(error, {
      tags: { feature: 'multiview', action: 'decodeTiles' },
      extra: { raw },
    })
    return { tiles: [], colors: {} }
  }
}

export const isCurrentFormat = (raw: string): boolean =>
  raw.startsWith(`${PERSISTENCE_VERSION}${FIELD_SEP}`)

export const saveSnapshotCache = (orgId: string, tiles: Tile[]): void => {
  try {
    const key = `${SNAPSHOT_STORAGE_PREFIX}${orgId}`
    const cache: Record<string, TileSnapshot> = {}
    for (const tile of tiles) {
      if (tile.snapshot.displayName || tile.snapshot.componentType) {
        cache[tile.tileId] = tile.snapshot
      }
    }
    if (Object.keys(cache).length > 0) {
      localStorage.setItem(key, JSON.stringify(cache))
    } else {
      localStorage.removeItem(key)
    }
  } catch {
    /* localStorage may be unavailable */
  }
}

export const loadSnapshotCache = (
  orgId: string
): Record<string, TileSnapshot> => {
  try {
    const key = `${SNAPSHOT_STORAGE_PREFIX}${orgId}`
    const raw = localStorage.getItem(key)
    if (!raw) return {}
    return JSON.parse(raw) as Record<string, TileSnapshot>
  } catch {
    return {}
  }
}

export const hydrateSnapshots = (
  tiles: Tile[],
  cache: Record<string, TileSnapshot>
): Tile[] =>
  tiles.map((tile) => {
    const cached = cache[tile.tileId]
    if (cached && !tile.snapshot.displayName) {
      return { ...tile, snapshot: { ...tile.snapshot, ...cached } }
    }
    return tile
  })
